import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box, Paper, Typography, TextField, Grid, Button, Chip,
  Accordion, AccordionSummary, AccordionDetails,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  CircularProgress, InputAdornment, FormControlLabel, Switch,
  Dialog, DialogTitle, DialogContent, DialogActions, ToggleButton, ToggleButtonGroup,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PrintIcon from "@mui/icons-material/Print";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import api from "../services/api";
import { openAuthPdf } from "../services/openAuthPdf";
import { todayISO } from "../utils/date";

const fmt = (n) => {
  const v = Number(n) || 0;
  const abs = Math.abs(v).toFixed(2);
  return (v < 0 ? "-$" : "$") + abs;
};

export default function GeneralLedger() {
  const today = todayISO();

  // Default view starts at today so the page doesn't pull historical
  // settlement entries (made when we cleaned the books) into the headline
  // Total Debit / Credit numbers. Users can widen the range whenever they
  // want a longer view.
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  const [search, setSearch] = useState("");          // live, client-side
  const [activeOnly, setActiveOnly] = useState(false); // hide zero-activity?
  const [allAccounts, setAllAccounts] = useState([]);
  const [loading, setLoading] = useState(false);

  // Server fetch only depends on date range — we filter client-side so
  // typing in the search bar is instant.
  const fetchLedger = useCallback(() => {
    setLoading(true);
    api.get("/generalLedger", { params: { fromDate, toDate } })
      .then((r) => setAllAccounts(Array.isArray(r.data?.accounts) ? r.data.accounts : []))
      .catch((e) => {
        console.error("General ledger fetch failed:", e);
        setAllAccounts([]);
      })
      .finally(() => setLoading(false));
  }, [fromDate, toDate]);

  useEffect(() => { fetchLedger(); }, [fetchLedger]);

  // Client-side filter — live as you type + optional active-only toggle
  const accounts = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = allAccounts;
    if (activeOnly) {
      list = list.filter((a) =>
        (a.transactions && a.transactions.length > 0) ||
        Math.abs(Number(a.opening) || 0) > 0.005
      );
    }
    if (q) list = list.filter((a) => (a.account || "").toLowerCase().includes(q));
    return list;
  }, [allAccounts, search, activeOnly]);

  const openPdf = () => {
    // Respect the on-screen search so "search a customer, then Print" yields
    // just that customer's itemised statement instead of every account.
    const params = new URLSearchParams({ fromDate, toDate });
    if (search.trim()) params.set("accountName", search.trim());
    openAuthPdf(`pdfReports/generalLedger?${params.toString()}`);
  };

  // Page totals (over the filtered set). Settlement entries are
  // bookkeeping cleanup, not real activity — they would otherwise dwarf
  // the headline Debit / Credit numbers with $30M+ of historical
  // zeroing-out. Exclude them from the totals but leave them in each
  // account's drill-down (where they remain visible for audit).
  const isSettlement = (tx) =>
    tx?.type === "Opening Balance Settled" ||
    tx?.type === "Broker Transfer" ||
    tx?.type === "Assigned to Broker";

  const realTotals = accounts.reduce((acc, a) => {
    let debit = 0, credit = 0;
    for (const tx of a.transactions || []) {
      if (isSettlement(tx)) continue;
      const amt = Number(tx.amount) || 0;
      if (amt >= 0) debit  += amt;
      else          credit += -amt;
    }
    acc.debit  += debit;
    acc.credit += credit;
    return acc;
  }, { debit: 0, credit: 0 });

  const pageDebit   = realTotals.debit;
  const pageCredit  = realTotals.credit;
  const pageClosing = accounts.reduce((s, a) => s + (Number(a.closing) || 0), 0);

  return (
    <Box p={2}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>General Ledger</Typography>
        <Button variant="outlined" startIcon={<PrintIcon />} onClick={openPdf}>
          Print PDF
        </Button>
      </Box>

      {/* Big search bar at the top — live filter as you type */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <TextField
          fullWidth
          autoFocus
          placeholder="Search by account name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <Box sx={{ mt: 1, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            Showing {accounts.length} of {allAccounts.length} account{allAccounts.length === 1 ? "" : "s"}
          </Typography>
          <FormControlLabel
            control={<Switch checked={activeOnly} onChange={(e) => setActiveOnly(e.target.checked)} />}
            label="Hide accounts with no activity"
          />
        </Box>
      </Paper>

      {/* Date range filter */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 5 }}>
            <TextField type="date" label="From" fullWidth InputLabelProps={{ shrink: true }}
              value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
          </Grid>
          <Grid size={{ xs: 12, sm: 5 }}>
            <TextField type="date" label="To" fullWidth InputLabelProps={{ shrink: true }}
              value={toDate} onChange={(e) => setToDate(e.target.value)} />
          </Grid>
          <Grid size={{ xs: 12, sm: 2 }}>
            <Button variant="contained" fullWidth onClick={fetchLedger}>Refresh</Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Totals strip */}
      <Paper elevation={3} sx={{ p: 2, mb: 2, bgcolor: "primary.dark", color: "primary.contrastText" }}>
        <Grid container spacing={2}>
          <Stat label="Accounts" value={accounts.length} count />
          <Stat label="Total Debit (+)"  value={pageDebit} />
          <Stat label="Total Credit (-)" value={pageCredit} />
          <Stat label="Net Position"     value={pageClosing} highlight />
        </Grid>
      </Paper>

      {loading ? (
        <Box sx={{ p: 4, textAlign: "center" }}><CircularProgress /></Box>
      ) : accounts.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: "center", color: "text.secondary" }}>
          No ledger activity in this range.
        </Paper>
      ) : (
        accounts.map((acc) => <AccountSection key={acc.account} acc={acc} onChanged={fetchLedger} />)
      )}
    </Box>
  );
}

function AccountSection({ acc, onChanged }) {
  const [editOpen, setEditOpen] = useState(false);
  const [bankOpen, setBankOpen] = useState(false);
  /* A balance within half a cent of zero is settled. Comparing against a
   * bare 0 made a closing of -0.000001 read as "-$0.00 (we owe)", which put
   * a payable label on an account that is square. */
  const SETTLED = 0.005;
  const closing = Math.abs(acc.closing || 0) < SETTLED ? 0 : (acc.closing || 0);
  const isReceivable = closing > 0;   // they owe us
  const isPayable    = closing < 0;   // we owe them
  const balanceColor =
    closing === 0 ? "success.main"
      : isReceivable ? "primary.main"
      : "error.main";

  return (
    <Accordion sx={{ mb: 1 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", pr: 1, flexWrap: "wrap", gap: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography sx={{ fontWeight: 700 }}>{acc.account}</Typography>
            <Button
              size="small"
              variant="text"
              startIcon={<EditIcon fontSize="inherit" />}
              onClick={(e) => { e.stopPropagation(); setEditOpen(true); }}
              sx={{ minWidth: 0, py: 0 }}
            >
              Set opening
            </Button>
            <Button
              size="small"
              variant="text"
              startIcon={<AccountBalanceIcon fontSize="inherit" />}
              onClick={(e) => { e.stopPropagation(); setBankOpen(true); }}
              sx={{ minWidth: 0, py: 0 }}
            >
              Bank entry
            </Button>
          </Box>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Chip
              size="small"
              label={`Closing: ${fmt(closing)} ${isReceivable ? "(they owe)" : isPayable ? "(we owe)" : "(settled)"}`}
              sx={{ fontWeight: 700, bgcolor: balanceColor, color: "#fff" }}
            />
          </Box>
        </Box>
      </AccordionSummary>
      <BankEntryDialog
        open={bankOpen}
        account={acc.account}
        currentBalance={acc.closing || 0}
        onClose={() => setBankOpen(false)}
        onSaved={() => { setBankOpen(false); onChanged && onChanged(); }}
      />
      <SetOpeningDialog
        open={editOpen}
        account={acc.account}
        currentOpening={acc.opening || 0}
        onClose={() => setEditOpen(false)}
        onSaved={() => { setEditOpen(false); onChanged && onChanged(); }}
      />
      <AccordionDetails>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Ref #</TableCell>
                <TableCell align="right">Debit (+)</TableCell>
                <TableCell align="right">Credit (-)</TableCell>
                <TableCell align="right">Balance</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow sx={{ bgcolor: "action.hover" }}>
                <TableCell colSpan={5} sx={{ fontStyle: "italic", color: "text.secondary" }}>
                  Opening
                </TableCell>
                <TableCell align="right" sx={{ fontStyle: "italic" }}>{fmt(acc.opening)}</TableCell>
              </TableRow>
              {(() => {
                let bal = Number(acc.opening) || 0;
                return acc.transactions.map((tx, i) => {
                  const amt = Number(tx.amount) || 0;
                  bal += amt;
                  return (
                    <TableRow key={i}>
                      <TableCell>{tx.date ? String(tx.date).split("T")[0] : "-"}</TableCell>
                      <TableCell>{tx.type}</TableCell>
                      <TableCell>{tx.ref}</TableCell>
                      <TableCell align="right" sx={{ color: amt > 0 ? "primary.main" : "inherit" }}>
                        {amt > 0 ? fmt(amt) : ""}
                      </TableCell>
                      <TableCell align="right" sx={{ color: amt < 0 ? "error.main" : "inherit" }}>
                        {amt < 0 ? fmt(-amt) : ""}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>{fmt(bal)}</TableCell>
                    </TableRow>
                  );
                });
              })()}
              <TableRow sx={{ bgcolor: "primary.light" }}>
                <TableCell colSpan={3} sx={{ fontWeight: 700 }}>Closing Balance</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>{fmt(acc.totalDebit)}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>{fmt(acc.totalCredit)}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>{fmt(acc.closing)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </AccordionDetails>
    </Accordion>
  );
}

/* ================= SET OPENING BALANCE DIALOG =================
 * Lets the user lock in a specific opening balance for an account by
 * inserting a settlement row that brings the running total to the
 * requested value. Positive number means "they owe us" (receivable);
 * negative or via the toggle "we owe them" (payable).
 * ================================================================= */
function SetOpeningDialog({ open, account, currentOpening, onClose, onSaved }) {
  const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
  const [direction, setDirection] = useState("receivable");
  const [amount, setAmount] = useState("");
  const [asOfDate, setAsOfDate] = useState(yesterday);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    // Seed inputs with whatever the current opening already is, so a small
    // tweak doesn't require typing the whole number from scratch.
    const current = Number(currentOpening) || 0;
    setDirection(current < 0 ? "payable" : "receivable");
    setAmount(current === 0 ? "" : Math.abs(current).toFixed(2));
    setAsOfDate(yesterday);
  }, [open, currentOpening, yesterday]);

  if (!open) return null;

  const signed = (Number(amount) || 0) * (direction === "payable" ? -1 : 1);

  const save = async () => {
    if (!Number.isFinite(Number(amount))) { alert("Enter an amount."); return; }
    try {
      setSaving(true);
      await api.post("/generalLedger/setOpeningBalance", {
        accountName:    account,
        desiredOpening: signed,
        asOfDate,
      });
      onSaved && onSaved();
    } catch (err) {
      alert("Failed: " + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Set opening balance — {account}</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>Current opening</Typography>
          <Typography sx={{ fontWeight: 700, fontSize: 18 }}>{fmt(currentOpening)}</Typography>
        </Box>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>Balance type</Typography>
            <ToggleButtonGroup
              fullWidth size="small" exclusive
              value={direction} onChange={(_, v) => v && setDirection(v)} sx={{ mt: 0.5 }}
            >
              <ToggleButton value="receivable" color="primary">
                They owe us (Receivable)
              </ToggleButton>
              <ToggleButton value="payable" color="error">
                We owe them (Payable)
              </ToggleButton>
            </ToggleButtonGroup>
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth label="Amount" type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              inputProps={{ step: "0.01", min: "0" }}
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth label="As of date" type="date"
              InputLabelProps={{ shrink: true }}
              value={asOfDate}
              onChange={(e) => setAsOfDate(e.target.value)}
              helperText="Settlement is dated here. Defaults to yesterday."
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 2 }}>
          <Chip
            size="small" variant="outlined" color={direction === "receivable" ? "primary" : "error"}
            label={`New opening will be ${fmt(signed)} ${direction === "receivable" ? "(they owe us)" : "(we owe them)"}`}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={saving} onClick={save}>
          {saving ? "Saving…" : "Save Opening Balance"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}


/* Bank money that is not paying an invoice: borrowing from a lender,
 * repaying them, or funding moved between the company's own accounts.
 *
 * Deliberately separate from Daily Cash. Daily Cash is the physical drawer,
 * so putting a bank transfer there leaves that day's cash count out by the
 * whole amount. Nothing here writes a Daily Cash line. */
function BankEntryDialog({ open, account, currentBalance, onClose, onSaved }) {
  const [direction, setDirection] = useState("received");
  const [amount, setAmount] = useState("");
  const [entryDate, setEntryDate] = useState(todayISO());
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDirection("received");
    setAmount("");
    setEntryDate(todayISO());
    setNote("");
  }, [open]);

  if (!open) return null;

  const value = Number(amount) || 0;
  const signed = direction === "received" ? -value : value;
  const after = (Number(currentBalance) || 0) + signed;
  const settled = Math.abs(after) < 0.005;

  const save = async () => {
    if (!(value > 0)) { alert("Enter an amount greater than zero."); return; }
    try {
      setSaving(true);
      await api.post("/generalLedger/bankEntry", {
        accountName: account,
        direction,
        amount: value,
        entryDate,
        note: note.trim() || undefined,
      });
      onSaved && onSaved();
    } catch (err) {
      alert("Failed: " + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Bank entry — {account}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
          For money that moved through the <strong>bank</strong> and is not paying an
          invoice — borrowing, repaying a loan, or moving funds between your own
          companies. Cash and checks belong in Daily Cash instead.
        </Typography>

        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>Balance now</Typography>
          <Typography sx={{ fontWeight: 700, fontSize: 18 }}>
            {fmt(currentBalance)}{" "}
            {(currentBalance || 0) < -0.005 ? "(we owe them)"
              : (currentBalance || 0) > 0.005 ? "(they owe us)" : "(settled)"}
          </Typography>
        </Box>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              Which way did the money go?
            </Typography>
            <ToggleButtonGroup
              fullWidth size="small" exclusive
              value={direction} onChange={(_, v) => v && setDirection(v)} sx={{ mt: 0.5 }}
            >
              <ToggleButton value="received" color="error">
                They sent us money
              </ToggleButton>
              <ToggleButton value="paid" color="primary">
                We sent them money
              </ToggleButton>
            </ToggleButtonGroup>
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth label="Amount" type="number" autoFocus
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              inputProps={{ step: "0.01", min: "0" }}
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth label="Date of transfer" type="date"
              InputLabelProps={{ shrink: true }}
              value={entryDate}
              onChange={(e) => setEntryDate(e.target.value)}
              helperText="The day it left or reached the bank."
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth label="Note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Borrowed by ACH"
              helperText="Shown on the ledger, so write what it was for."
            />
          </Grid>
        </Grid>

        {value > 0 && (
          <Box sx={{ mt: 2 }}>
            <Chip
              size="small" variant="outlined"
              color={settled ? "success" : after < 0 ? "error" : "primary"}
              label={`Balance will become ${fmt(settled ? 0 : after)} ${
                settled ? "(settled)" : after < 0 ? "(we owe them)" : "(they owe us)"}`}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={saving || !(value > 0)} onClick={save}>
          {saving ? "Saving…" : "Record Bank Entry"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function Stat({ label, value, count, highlight }) {
  return (
    <Grid size={{ xs: 6, sm: 3 }}>
      <Box sx={{ p: 1, borderRadius: 1, bgcolor: highlight ? "warning.main" : "transparent", color: highlight ? "warning.contrastText" : "inherit" }}>
        <Typography variant="caption" sx={{ opacity: 0.85 }}>{label}</Typography>
        <Typography sx={{ fontWeight: 700, fontSize: 20 }}>
          {count ? value : fmt(value)}
        </Typography>
      </Box>
    </Grid>
  );
}
